import { enhanceError } from '@allurion/utils';
import { useState, useEffect, useLayoutEffect, useCallback, useRef } from 'react';
import { useIntl } from 'react-intl';
import { Room, Participant, connect } from 'twilio-video';

import { useTrackEvent } from 'src/analytics/analytics';
import { toastError } from 'src/components/ui/toasts';
import { useCurrentProvider } from 'src/hooks/useCurrentProvider';
import { Logger } from 'src/services/Logger';
import { sendNotification } from 'src/twilio/TwilioApi';
import { VIDEO_SIZE } from 'src/utils/constants';

import { getTwilioToken } from './twilio-token';

const timeout = 5000 * 60; // 5mins

export function useTwilioVideo({
  timeoutCallback,
  patientUsername,
}: {
  timeoutCallback?: () => void;
  patientUsername?: string;
}) {
  const intl = useIntl();

  const { provider } = useCurrentProvider();
  const providerUsername = provider.CognitoUserName;
  const { trackTwilioFailedVideoCall } = useTrackEvent();

  const [room, setRoom] = useState<Room | null>(null);
  const [participants, setParticipants] = useState<Participant[] | []>([]);
  const [muteAudio, setMuteAudio] = useState<boolean>(false);
  const [stopVideo, setStopVideo] = useState<boolean>(false);
  const [remoteAudio, setRemoteAudio] = useState<boolean>(true);
  const [remoteVideo, setRemoteVideo] = useState<boolean>(true);
  const [isCallOver, setIsCallOver] = useState<boolean>(true);
  const [startCall, setStartCall] = useState<boolean>(false);

  // Per-instance timeout handle. Previously a module-level `let timer` was shared
  // across every mounted component, so two concurrent calls would clobber each
  // other's auto-hang-up timeout.
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  // Mirror `room` into a ref so a stable `disconnectVideoCall` (and the auto-hang-up
  // timeout that invokes it) always acts on the *current* room. The timeout is armed
  // when `startCall` flips true — typically before `room` is set — so a
  // disconnectVideoCall that closed over `room` captured `room === null`, and the
  // already-armed timer held that stale closure (the `!timerRef.current` guard blocks
  // re-arming). When it fired for an unanswered call it early-returned and never
  // disconnected. Reading `roomRef.current` keeps the callback correct without
  // depending on `room`.
  const roomRef = useRef<Room | null>(null);

  // Sync in a layout effect (not a passive effect) so the ref is updated
  // synchronously at commit, *before* paint. A passive `useEffect` is flushed
  // after paint, leaving a window where `room` has just become non-null but
  // `roomRef.current` is still stale: a user clicking "hang up" in that window
  // would hit `disconnectVideoCall()`'s no-room early return, leaving the live
  // Twilio room connected and `room` state non-null. useLayoutEffect closes it.
  useLayoutEffect(() => {
    roomRef.current = room;
  }, [room]);

  // Clear any pending auto-hang-up timeout on unmount so it can't fire after the
  // component is gone. Otherwise a timeout armed while `startCall` is true would
  // call disconnectVideoCall()/state setters on an unmounted component (React
  // warnings + a stray disconnect attempt).
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, []);

  const disconnectVideoCall = useCallback(() => {
    // Always reset call state and clear any pending auto-hang-up timeout first,
    // even when no room was ever produced (a join that never yielded a room, or
    // the user cancels while still connecting). If we early-returned in that case
    // the hook would stay stuck with `startCall === true` and the timeout effect
    // could re-arm on a later re-render. Only the actual Twilio room teardown is
    // conditional on a room existing.
    setStartCall(false);
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }

    const currentRoom = roomRef.current;

    if (!currentRoom) {
      return;
    }

    // @ts-ignore FIXME: TS says stop method doesn't exist
    currentRoom.localParticipant.tracks.forEach((trackPub) => trackPub.track.stop());
    currentRoom.removeAllListeners();
    currentRoom.disconnect();

    setRoom(null);
  }, []);

  useEffect(() => {
    const onParticipantConnected = (participant: Participant) => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      setParticipants((prev) => [...prev, participant]);
      setIsCallOver(false);
    };

    const onParticipantDisconnected = (participant: Participant) => {
      setParticipants((prev) => prev.filter((p) => p !== participant));
      setIsCallOver(true);
    };

    const onParticipantReconnected = () => {
      setIsCallOver(false);
    };

    const onParticipantReconnecting = () => {
      setIsCallOver(true);
    };

    const onTrackDisabled = ({ kind }: { kind: string }) => {
      if (kind === 'video') {
        setRemoteVideo(false);
      } else if (kind === 'audio') {
        setRemoteAudio(false);
      }
    };

    const onTrackEnabled = ({ kind }: { kind: string }) => {
      if (kind === 'video') {
        setRemoteVideo(true);
      } else if (kind === 'audio') {
        setRemoteAudio(true);
      }
    };

    if (room) {
      room.on('participantConnected', onParticipantConnected);
      room.on('participantDisconnected', onParticipantDisconnected);
      room.on('participantReconnected', onParticipantReconnected);
      room.on('participantReconnecting', onParticipantReconnecting);
      room.on('trackDisabled', onTrackDisabled);
      room.on('trackEnabled', onTrackEnabled);
      room.participants?.forEach(onParticipantConnected);
      room.localParticipant?.audioTracks?.forEach(
        ({ isTrackEnabled }: { isTrackEnabled: boolean }) => setMuteAudio(!isTrackEnabled)
      );
      room.localParticipant?.videoTracks?.forEach(
        ({ isTrackEnabled }: { isTrackEnabled: boolean }) => setStopVideo(!isTrackEnabled)
      );
    }

    return () => {
      participants?.map((p) => p?.removeAllListeners());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [room]);

  useEffect(() => {
    const beginTimeout = () => {
      timerRef.current = setTimeout(() => {
        // Clear the ref first: the handle is spent once the callback runs, and
        // disconnectVideoCall() returns early when there's no room (e.g. a failed
        // join), which would otherwise leave timerRef.current truthy and block the
        // `startCall && !timerRef.current` guard from ever re-arming the timer.
        timerRef.current = null;
        disconnectVideoCall();
        timeoutCallback?.();
      }, timeout);
    };

    if (startCall && !timerRef.current) {
      beginTimeout();
    }
  }, [disconnectVideoCall, startCall, timeoutCallback]);

  useEffect(() => {
    const joinRoom = async (providerUsername: string) => {
      const roomConfig = {
        name: `${providerUsername}_${patientUsername}`,
        audio: true,
        video: VIDEO_SIZE,
      };
      const token = await getTwilioToken(providerUsername);
      const roomData = await connect(token, roomConfig);

      if (roomData) {
        const notification = {
          type: 1,
          identity: patientUsername,
          roomId: `${providerUsername}_${patientUsername}`,
          body: 'Incoming Video Consult', // <- see how to localize from the Allurion app
        };

        await sendNotification(notification);
        setRoom(roomData);
        setIsCallOver(false);
      }
    };

    const shouldJoinRoom = !room && patientUsername && startCall && providerUsername;

    if (shouldJoinRoom) {
      joinRoom(providerUsername).catch((error) => {
        Logger.captureException(enhanceError(error, { prefix: 'useTwilioVideo' }));
        trackTwilioFailedVideoCall({ error });
        toastError(
          intl.formatMessage({
            defaultMessage: 'Cannot join video call. Please try again.',
            id: 'twilio.failed-to-join-video-call',
          })
        );
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [room, patientUsername, startCall, providerUsername]);

  return {
    room,
    setRoom,
    participants,
    muteAudio,
    setMuteAudio,
    stopVideo,
    setStopVideo,
    isCallOver,
    setIsCallOver,
    remoteAudio,
    setRemoteAudio,
    remoteVideo,
    setRemoteVideo,
    startCall,
    setStartCall,
    disconnectVideoCall,
  };
}
